import type { SignalingMessage } from '@/transport/protocol';
import type { ChannelClosure, ClosureKind } from '@/transport/ws-channel';
import { PeerSession, describeIncoming, type PeerConnectionFactory } from './peer-session';
import { ReconnectGuard, reconnectDelayMs } from './reconnect-policy';
import { isBotClientId, type CallEndReason, type CallPhase } from './types';

/**
 * Оркестратор дзвінка: роль ініціатора, перебудова зʼєднання, реконект.
 *
 * Свідомо не знає ні про React, ні про DOM — про стан повідомляє колбеком.
 */

export interface SessionDeps {
  clientId: string;
  /** Надіслати повідомлення в сигнальний канал. */
  send: (payload: Record<string, unknown>) => void;
  /** Перепідключити сигнальний канал. Повертає true, якщо канал відкрився. */
  reconnectChannel: () => Promise<boolean>;
  getIceConfiguration: () => Promise<RTCConfiguration>;
  getLocalStream: () => MediaStream | null;
  peerConnectionFactory?: PeerConnectionFactory;
  onPhase: (phase: CallPhase, reason?: CallEndReason) => void;
  onRemoteStream: (stream: MediaStream | null) => void;
  onPeerIsBot: (isBot: boolean) => void;
  /** Співрозмовник повідомив про свій стан мікрофона/камери. */
  onPeerMedia?: (state: { micEnabled: boolean; videoEnabled: boolean }) => void;
  /** Співрозмовник просить змінити наш стан. Виконуємо лише те, що вміє браузер. */
  onRemoteControl?: (action: string, value: boolean) => void;
  onReconnectAttempt?: (attempt: number) => void;
  /** Пауза між спробами. Виділена, щоб тести не чекали реального часу. */
  delay?: (ms: number) => Promise<void>;
}

const realDelay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export class CallSession {
  private peer: PeerSession | null = null;
  private readonly guard = new ReconnectGuard();
  private isInitiator = false;
  private initiatorAssigned = false;
  private ended = false;
  /** Свідомий вихід співрозмовника: відновлювати нічого не треба. */
  private peerLeft = false;

  constructor(private readonly deps: SessionDeps) {}

  get reconnectAttemptsUsed(): number {
    return this.guard.attemptsUsed;
  }

  /** Представлення серверу. Саме воно визначає, чи станемо ми ініціатором. */
  announce(isWebKit: boolean, deviceType: string): void {
    // Сервер розрізняє `safari_web` і `non_safari`, і за наявності non-Safari
    // ініціатором робить саме його. WebKit мусить представлятися чесно —
    // інакше дзвінок із мобільним клієнтом просто не встане.
    this.deps.send(
      isWebKit
        ? { type: 'safari_ready', clientId: this.deps.clientId, platform: 'safari_web' }
        : { type: 'hello', clientId: this.deps.clientId },
    );
    this.deps.send({
      type: 'device_info',
      clientId: this.deps.clientId,
      deviceType,
    });
  }

  async handleMessage(message: SignalingMessage): Promise<void> {
    if (this.ended) return;

    if (message.kind === 'typed') {
      switch (message.type) {
        case 'server_initiator_assignment':
          await this.applyInitiatorAssignment(message.initiatorId);
          return;
        case 'participant_reconnected':
          await this.rebuildForNewParticipant(message.clientId);
          return;
        case 'participant_left':
          this.handleParticipantLeft(message.clientId);
          return;
        case 'media_state':
          // Стан співрозмовника: показуємо, що в нього вимкнено. Поля
          // необовʼязкові — клієнт іншої версії може надіслати лише частину.
          this.deps.onPeerMedia?.({
            micEnabled: message.micEnabled ?? true,
            videoEnabled: message.videoEnabled ?? true,
          });
          return;
        case 'remote_control':
          this.handleRemoteControl(message.action, message.value);
          return;
        default:
          return;
      }
    }

    const incoming = describeIncoming(message);
    if (!incoming) return;

    // Власне ехо: сервер розсилає всім, крім відправника, але клієнт іншої
    // версії міг покласти наш clientId у поле — перевіряємо явно.
    if ('clientId' in message && message.clientId === this.deps.clientId) return;

    // Зʼєднання може ще не існувати, якщо пропозиція випередила призначення
    // ролі. Створюємо на місці, а не втрачаємо повідомлення.
    const peer = await this.ensurePeer();

    switch (incoming.kind) {
      case 'offer':
        await peer.acceptOffer(incoming.description);
        return;
      case 'answer':
        await peer.acceptAnswer(incoming.description);
        return;
      case 'candidate':
        await peer.addRemoteCandidate(incoming.candidate);
        return;
    }
  }

  /**
   * Роль призначає сервер. Пропозиція створюється ВИКЛЮЧНО за власним
   * призначенням — ініціатива з обох боків означає, що дзвінок не встане.
   */
  private async applyInitiatorAssignment(initiatorId: string): Promise<void> {
    this.initiatorAssigned = true;
    this.isInitiator = initiatorId === this.deps.clientId;

    // Зʼєднання створюють ОБИДВІ сторони. Не-ініціатор мусить бути готовий
    // прийняти пропозицію в момент її надходження; якщо створювати зʼєднання
    // лише ініціатору, вхідна пропозиція просто нікуди не потрапляє —
    // дзвінок «майже встає» і тихо лишається в очікуванні.
    const peer = await this.ensurePeer();
    if (!this.isInitiator) return;

    await peer.createOffer();
  }

  /**
   * Приєднався другий учасник.
   *
   * ⚠️ Сервер шле `participant_reconnected` щоразу, коли в кімнаті стало
   * більше одного учасника — не лише при поверненні того самого `client_id`.
   * Саме так відбувається заміна бота живою людиною, тож зʼєднання
   * перебудовується ЗАВЖДИ, навіть якщо поточне виглядає робочим.
   */
  private async rebuildForNewParticipant(clientId: string): Promise<void> {
    this.peerLeft = false;
    this.deps.onPeerIsBot(isBotClientId(clientId));

    this.releasePeer();
    this.deps.onRemoteStream(null);
    this.initiatorAssigned = false;
    this.isInitiator = false;

    await this.ensurePeer();
    this.deps.onPhase(isBotClientId(clientId) ? 'waiting-with-bot' : 'connecting');
    // Пропозицію не створюємо: чекаємо на нове призначення ініціатора.
  }

  /**
   * Команда віддаленого керування від співрозмовника.
   *
   * Браузер уміє лише мікрофон і камеру: `switch_camera` та керування
   * гучномовцем тут виконати нічим, тож вони мовчки ігноруються — і, що
   * важливо, підтвердження про виконання не надсилається.
   */
  private handleRemoteControl(action: string | undefined, value: unknown): void {
    if (action !== 'mic' && action !== 'video') return;
    if (typeof value !== 'boolean') return;
    this.deps.onRemoteControl?.(action, value);
  }

  private handleParticipantLeft(clientId: string): void {
    // Вихід бота — не вихід співрозмовника: сервер його навіть не анонсує,
    // але клієнт іншої версії може, тож перевіряємо самі.
    if (isBotClientId(clientId)) {
      this.deps.onPeerIsBot(false);
      return;
    }
    this.peerLeft = true;
    this.end('peer-left');
  }

  private async ensurePeer(): Promise<PeerSession> {
    const existing = this.peer;
    if (existing) return existing;

    const configuration = await this.deps.getIceConfiguration();
    const peer = new PeerSession(
      configuration,
      {
        onLocalDescription: (description) => {
          const key = description.type === 'offer' ? 'offer' : 'answer';
          this.deps.send({ [key]: description, clientId: this.deps.clientId });
        },
        onLocalCandidate: (candidate) => {
          this.deps.send({ candidate, clientId: this.deps.clientId });
        },
        onRemoteTrack: (stream) => {
          this.deps.onRemoteStream(stream);
          this.deps.onPhase('connected');
        },
        onConnectionLost: () => {
          void this.handleConnectionLoss();
        },
        onConnected: () => {
          this.deps.onPhase('connected');
        },
      },
      this.deps.peerConnectionFactory,
    );

    const stream = this.deps.getLocalStream();
    if (stream) peer.addLocalStream(stream);

    this.peer = peer;
    return peer;
  }

  /**
   * Єдина точка входу для ВСІХ сигналів про втрату звʼязку: стан зʼєднання,
   * стан ICE, закриття сокета, події вкладки й мережі браузера.
   */
  async handleConnectionLoss(): Promise<void> {
    if (this.ended || this.peerLeft) return;

    const started = await this.guard.run(async (attempt) => {
      this.deps.onPhase('reconnecting');
      this.deps.onReconnectAttempt?.(attempt);

      await (this.deps.delay ?? realDelay)(reconnectDelayMs(attempt));
      if (this.ended || this.peerLeft) return true;

      // Ресурси попередньої спроби звільняються ДО створення нових, інакше
      // події мертвого зʼєднання продовжують правити стан дзвінка.
      this.releasePeer();
      this.deps.onRemoteStream(null);
      this.initiatorAssigned = false;

      const channelOpen = await this.deps.reconnectChannel();
      if (!channelOpen) return false;

      await this.ensurePeer();
      return true;
    });

    if (!started && this.guard.isExhausted) {
      this.end('reconnect-failed');
    }
  }

  /** Закриття сигнального каналу — теж сигнал про втрату, але не завжди. */
  async handleChannelClosure(closure: ChannelClosure): Promise<void> {
    const terminal: Partial<Record<ClosureKind, CallEndReason>> = {
      'room-full': 'room-full',
      'invalid-room': 'invalid-room',
    };
    const reason = terminal[closure.kind];
    if (reason) {
      this.end(reason);
      return;
    }
    // `replaced`: наш сокет замінено іншим із тим самим client_id —
    // відновлювати нема чого, інакше два сокети битимуться за місце.
    if (closure.kind === 'replaced') return;

    await this.handleConnectionLoss();
  }

  /**
   * Заміна відеотреку, що передається співрозмовнику: камера ↔ демонстрація
   * екрана. Через `replaceTrack()`, тож зʼєднання не перестворюється і
   * повторне узгодження не потрібне.
   */
  async replaceVideoTrack(track: MediaStreamTrack | null): Promise<void> {
    await this.peer?.replaceTrack('video', track);
  }

  private releasePeer(): void {
    const peer = this.peer;
    this.peer = null;
    peer?.dispose();
  }

  end(reason: CallEndReason): void {
    if (this.ended) return;
    this.ended = true;
    this.releasePeer();
    this.deps.onRemoteStream(null);
    this.guard.reset();
    this.deps.onPhase('ended', reason);
  }

  get isEnded(): boolean {
    return this.ended;
  }

  get hasInitiatorRole(): boolean {
    return this.initiatorAssigned && this.isInitiator;
  }

  get awaitingInitiatorAssignment(): boolean {
    return !this.initiatorAssigned;
  }
}
